import { Component } from '@theme/component';
import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Desk mat bundle picker: 1 mat, 2 mats + 1 free matching coaster set (shopper picks which),
 * or 3 mats + 3 free matching coaster sets. Adds every line in one /cart/add.js call.
 * The coaster price is taken off at checkout by the automatic Buy X Get Y discounts.
 *
 * @typedef {{ v: number, t: string, img: string, p: number, cv: number, ct: string, cp: number }} BundleMat
 * @typedef {{ currency: string, current: BundleMat, mats: BundleMat[] }} BundleData
 *
 * @typedef {object} CoasterBundleRefs
 * @property {HTMLScriptElement} data
 * @property {HTMLElement} builder
 * @property {HTMLElement} slot3
 * @property {HTMLSelectElement} select2
 * @property {HTMLSelectElement} select3
 * @property {HTMLImageElement} thumb2
 * @property {HTMLImageElement} thumb3
 * @property {HTMLElement} freeChoice
 * @property {HTMLElement} freeAll
 * @property {HTMLElement} freeLabel1
 * @property {HTMLElement} freeLabel2
 * @property {HTMLElement} total
 * @property {HTMLElement} compare
 * @property {HTMLButtonElement} addButton
 * @property {HTMLElement} error
 *
 * @extends {Component<CoasterBundleRefs>}
 */
class CoasterBundleComponent extends Component {
  requiredRefs = ['data', 'builder', 'select2', 'select3', 'total', 'addButton', 'error'];

  /** @type {BundleData} */
  #data = { currency: 'USD', current: /** @type {any} */ (null), mats: [] };

  /** @type {Intl.NumberFormat | null} */
  #formatter = null;

  connectedCallback() {
    super.connectedCallback();

    try {
      this.#data = JSON.parse(this.refs.data.textContent || '{}');
    } catch (error) {
      console.warn('[coaster-bundle] Invalid bundle data', error);
    }

    try {
      this.#formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.#data.currency || 'USD',
      });
    } catch {
      this.#formatter = null;
    }

    this.#render();
  }

  /** Re-renders whenever a tier, mat or free-coaster choice changes. */
  handleChange() {
    this.#render();
  }

  /** @returns {number} */
  get #tier() {
    const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-bundle-tier]:checked'));
    return Number(checked?.value) || 1;
  }

  /** @returns {BundleMat[]} The mats in the bundle for the current tier, in slot order. */
  get #selectedMats() {
    const { mats, current } = this.#data;
    const second = mats[Number(this.refs.select2.value)];
    const third = mats[Number(this.refs.select3.value)];
    return [current, second, third].filter(Boolean).slice(0, this.#tier);
  }

  /** @returns {BundleMat[]} The mats whose coaster sets come free. */
  get #freeCoasterMats() {
    const selected = this.#selectedMats;
    if (this.#tier === 3) return selected;
    if (this.#tier === 2) {
      const checked = /** @type {HTMLInputElement | null} */ (this.querySelector('input[data-bundle-free]:checked'));
      const index = Math.max(0, (Number(checked?.value) || 1) - 1);
      const mat = selected[index] ?? selected[0];
      return mat ? [mat] : [];
    }
    return [];
  }

  /** @param {number} cents */
  #money(cents) {
    const amount = cents / 100;
    return this.#formatter ? this.#formatter.format(amount) : `$${amount.toFixed(2)}`;
  }

  #render() {
    const tier = this.#tier;
    const { refs } = this;
    const { mats } = this.#data;

    this.dataset.tier = String(tier);
    refs.builder.hidden = tier === 1;
    if (refs.slot3) refs.slot3.hidden = tier < 3;
    if (refs.freeChoice) refs.freeChoice.hidden = tier !== 2;
    if (refs.freeAll) refs.freeAll.hidden = tier !== 3;

    const second = mats[Number(refs.select2.value)];
    const third = mats[Number(refs.select3.value)];
    if (refs.thumb2 && second?.img) refs.thumb2.src = second.img;
    if (refs.thumb3 && third?.img) refs.thumb3.src = third.img;
    if (refs.freeLabel1) refs.freeLabel1.textContent = this.#data.current?.ct ?? '';
    if (refs.freeLabel2) refs.freeLabel2.textContent = second?.ct ?? '';

    const matTotal = this.#selectedMats.reduce((sum, mat) => sum + mat.p, 0);
    const freeValue = this.#freeCoasterMats.reduce((sum, mat) => sum + mat.cp, 0);
    refs.total.textContent = this.#money(matTotal);
    if (refs.compare) {
      refs.compare.textContent = freeValue > 0 ? this.#money(matTotal + freeValue) : '';
      refs.compare.hidden = freeValue === 0;
    }

    this.#showError('');
  }

  /** @param {string} message */
  #showError(message) {
    this.refs.error.textContent = message;
    this.refs.error.hidden = !message;
  }

  /** @returns {Array<{ id: number, quantity: number }>} */
  #buildItems() {
    /** @type {Map<number, number>} */
    const lines = new Map();
    const add = (/** @type {number} */ id) => lines.set(id, (lines.get(id) ?? 0) + 1);

    for (const mat of this.#selectedMats) add(mat.v);
    for (const mat of this.#freeCoasterMats) add(mat.cv);

    return Array.from(lines, ([id, quantity]) => ({ id, quantity }));
  }

  async #refreshCart() {
    /** @type {any} */
    const cartItemsComponent = document.querySelector('cart-items-component');
    if (cartItemsComponent) {
      await customElements.whenDefined('cart-items-component');
      return cartItemsComponent.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);
    return response.json();
  }

  async addBundle() {
    const items = this.#buildItems();
    if (items.length === 0 || this.refs.addButton.disabled) return;

    const { addButton } = this.refs;
    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');
    this.#showError('');

    /** @type {string[]} */
    const sectionIds = [];
    for (const element of document.querySelectorAll('cart-items-component')) {
      if (element instanceof HTMLElement && element.dataset.sectionId) sectionIds.push(element.dataset.sectionId);
    }

    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        const message = result.description || result.message || 'Could not add the bundle to your cart.';
        this.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
        this.#showError(message);

        const cart = await this.#refreshCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { didError: true, items: cart.items, source: 'coaster-bundle', itemCount: totalQuantity },
        });
        return;
      }

      const cart = await this.#refreshCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          didError: false,
          items: cart.items,
          source: 'coaster-bundle',
          itemCount: totalQuantity,
          sections: result.sections,
        },
      });

      // Show the whole bundle (mats + free coasters) right away; fall back to the cart page.
      /** @type {any} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) {
        if (!drawer.isOpen) drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url;
      }
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
      this.#showError('Something went wrong. Please try again.');
    } finally {
      addButton.disabled = false;
      addButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('coaster-bundle-component')) {
  customElements.define('coaster-bundle-component', CoasterBundleComponent);
}
